import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import client from "../../../shared/api/client";
import DestructiveConfirmModal from "../../../shared/DestructiveConfirmModal";
import ArtistPickerField from "../../shared/components/ArtistPickerField";
import { fetchArtistOptions } from "../../shared/components/ArtistSelectComponents";
import RosterCard from "./RosterCard";

const AgentRoster = ({ agentName, artists: initialArtists, addUrl, teamId, canAdd, canRemove }) => {
  const [artists, setArtists] = useState(initialArtists || []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState(null);
  const restorePosition = useRef({});

  // Shared artist search with roster state layered on: artists already
  // represented render disabled with an "On roster" badge.
  const fetchRosterOptions = (query, callback) => {
    fetchArtistOptions(query, (options) => {
      callback((options || []).map((option) => (
        artists.some((artist) => artist.id === option.value)
          ? { ...option, disabled: true, badge: "On roster" }
          : option
      )));
    });
  };

  // Returned, not caught: ArtistPickerField toasts a rejection and keeps the
  // modal open so the user can retry.
  // Dedupe in the updater: the picker's "Use that artist instead" shortcut
  // (V3NewArtistPanel) submits an artist id without passing through the
  // disabled-option check, and create is idempotent — so a re-add returns the
  // existing card and would otherwise render twice under one React key.
  const add = (selection) => client.post(addUrl, { artist_id: selection.artistId })
    .then((res) => setArtists((list) => (
      list.some((existing) => existing.id === res.data.id) ? list : list.concat([res.data])
    )));

  const remove = (artist) => {
    setError(null);
    setArtists((list) => {
      const index = list.findIndex((existing) => existing.id === artist.id);
      restorePosition.current[artist.id] = index;
      return list.filter((existing) => existing.id !== artist.id);
    });

    client.delete(artist.removeUrl)
      .catch(() => {
        setArtists((list) => {
          if (list.some((existing) => existing.id === artist.id)) return list;
          const restored = list.slice();
          restored.splice(restorePosition.current[artist.id] ?? list.length, 0, artist);
          return restored;
        });
        setError(`Could not remove ${artist.name} from this roster.`);
      });
  };

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h5 className="mb-0 font-weight-bold">
          Artist roster <span className="social-dash-count">({artists.length})</span>
        </h5>
        {canAdd && (
          <button
            type="button"
            className="dash-card-action"
            onClick={() => setPickerOpen(true)}
            title="Add an artist"
            aria-label="Add an artist"
            aria-haspopup="dialog"
            data-testid="add-artist-btn"
          >
            <i className="fa fa-plus" />
          </button>
        )}
      </div>
      <p className="roster-subtitle mb-3">
        {`Artists ${agentName} represents. Changes sync to each artist's contacts.`}
      </p>

      {error && <div className="setlist-error mb-2" data-testid="roster-error">{error}</div>}

      {artists.length === 0 ? (
        <div className="dash-empty-state text-center">
          <span className="dash-empty-icon"><i className="fa fa-music" /></span>
          <h6 className="dash-empty-title mt-3 mb-1">No artists on this roster</h6>
          <p className="dash-empty-desc mb-3">{`Add an artist to start tracking shows through ${agentName}.`}</p>
          {canAdd && (
            <button type="button" className="btn btn-primary btn-sm dash-empty-action" onClick={() => setPickerOpen(true)}>
              Add an artist
            </button>
          )}
        </div>
      ) : (
        <div className="roster-grid" data-testid="roster-grid">
          {artists.map((artist) => (
            <RosterCard
              key={artist.id}
              artist={artist}
              removeControl={canRemove && artist.removeUrl ? (
                <DestructiveConfirmModal
                  trigger={
                    <button
                      type="button"
                      className="roster-card-remove"
                      title={`Remove ${artist.name}`}
                      aria-label={`Remove ${artist.name}`}
                      data-testid="roster-remove"
                    >
                      <i className="far fa-trash-alt" />
                    </button>
                  }
                  title="Remove from roster?"
                  body={`Remove ${artist.name} from ${agentName}'s roster? The artist record itself is kept.`}
                  confirmLabel="Remove"
                  onConfirm={() => remove(artist)}
                />
              ) : null}
            />
          ))}
        </div>
      )}

      {canAdd && (
        <ArtistPickerField
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          teamId={teamId}
          description={`Search for an artist to add to ${agentName}'s roster, or add a new one inline.`}
          fetchOptions={fetchRosterOptions}
          onSelect={add}
        />
      )}
    </React.Fragment>
  );
};

AgentRoster.propTypes = {
  agentName: PropTypes.string.isRequired,
  artists: PropTypes.array,
  addUrl: PropTypes.string.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canAdd: PropTypes.bool,
  canRemove: PropTypes.bool,
};

AgentRoster.defaultProps = { artists: [], teamId: null, canAdd: false, canRemove: false };

export default AgentRoster;
